import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import BaseTable from './BaseTable'
import CardList from './CardList'
import TableHeader from './TableHeader'
import TablePagination from './TablePagination'
import TableEmptyState from './TableEmptyState'
import useResponsiveTable from './useResponsiveTable'
import { getPriorityConfig } from './columnPriority'
import styles from './ResponsiveTable.module.scss'

/**
 * ResponsiveTable - Orchestrator component that renders a traditional table on
 * desktop (>1024px) and a card-based list on mobile/tablet (<=1024px).
 *
 * Drop-in replacement for direct BaseTable usage. Accepts the same column
 * definitions and adds responsive behavior via the variant prop.
 */
const ResponsiveTable = ({
  columns = [],
  data = [],
  loading = false,
  searched = false,
  sortField,
  sortDirection = 'asc',
  onSort,
  onRowClick,
  variant = 'calls',
  emptyState,
  loadingState,
  colSpan,
  containerProps = {},
  tableProps = {},

  headerTitle,
  headerIcon,
  headerResultCount,
  headerResultLabel,
  headerActions,
  renderHeaderContent,

  page,
  totalPages,
  onPageChange,
  limit,
  onLimitChange,
  limitOptions,

  rowHeaderColumn = 0
}) => {
  const { isCompact } = useResponsiveTable()
  const [viewState, setViewState] = useState('visible')
  const prevCompact = useRef(isCompact)

  /* eslint-disable react-hooks/set-state-in-effect -- animation state transition on layout change */
  useEffect(() => {
    if (prevCompact.current !== isCompact) {
      setViewState('exit')
      const exitTimer = setTimeout(() => {
        setViewState('enter')
        const enterTimer = setTimeout(() => {
          setViewState('visible')
        }, 50)
        return () => clearTimeout(enterTimer)
      }, 150)
      prevCompact.current = isCompact
      return () => clearTimeout(exitTimer)
    }
    return undefined
  }, [isCompact])
  /* eslint-enable react-hooks/set-state-in-effect */

  const priorityConfig = getPriorityConfig(variant)

  const viewClass = viewState === 'enter'
    ? styles.viewEnter
    : viewState === 'exit'
      ? styles.viewExit
      : styles.viewVisible

  const showHeader = headerTitle || headerIcon || headerResultCount || headerActions

  const showPagination = page && totalPages && totalPages > 1 && onPageChange && data.length > 0

  const renderMobileLoading = () => (
    <div className={styles.skeletonCardList}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={`skeleton-card-${i}`} className={styles.skeletonCard}>
          <div style={{ width: '40%', height: 12, background: 'var(--color-bg-gray)', borderRadius: 6, marginBottom: 8 }} />
          <div style={{ width: '70%', height: 18, background: 'var(--color-bg-gray)', borderRadius: 6, marginBottom: 4 }} />
          <div style={{ width: '50%', height: 12, background: 'var(--color-bg-gray)', borderRadius: 6, marginBottom: 12 }} />
          <div className={styles.skeletonCardGrid}>
            <div style={{ width: '80%', height: 14, background: 'var(--color-bg-gray)', borderRadius: 6 }} />
            <div style={{ width: '60%', height: 14, background: 'var(--color-bg-gray)', borderRadius: 6 }} />
            <div style={{ width: '70%', height: 14, background: 'var(--color-bg-gray)', borderRadius: 6 }} />
            <div style={{ width: '50%', height: 14, background: 'var(--color-bg-gray)', borderRadius: 6 }} />
          </div>
        </div>
      ))}
    </div>
  )

  if (isCompact) {
    return (
      <div className={`${styles.responsiveContainer} ${viewClass}`}>
        {/* Header */}
        {showHeader && (
          <TableHeader
            title={headerTitle}
            icon={headerIcon}
            resultCount={headerResultCount}
            resultLabel={headerResultLabel}
            actions={headerActions}
            isCompact
            columns={columns}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
          />
        )}

        {/* Custom header content fallback */}
        {!showHeader && renderHeaderContent && renderHeaderContent()}

        {/* Content */}
        {loading
          ? renderMobileLoading()
          : data.length === 0
            ? (emptyState || <TableEmptyState searched={searched} />)
            : (
              <CardList
                data={data}
                columns={columns}
                priorityConfig={priorityConfig}
                onRowClick={onRowClick}
              />
              )}

        {/* Pagination */}
        {showPagination && (
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
            limit={limit}
            onLimitChange={onLimitChange}
            limitOptions={limitOptions}
            isCompact
          />
        )}
      </div>
    )
  }

  return (
    <div className={`${styles.responsiveContainer} ${styles.responsiveContainerDesktop} ${viewClass}`}>
      <BaseTable
        columns={columns}
        data={data}
        loading={loading}
        searched={searched}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        onRowClick={onRowClick}
        emptyState={emptyState}
        loadingState={loadingState}
        colSpan={colSpan || columns.length}
        containerProps={containerProps}
        tableProps={tableProps}
        renderHeaderContent={renderHeaderContent}
        rowHeaderColumn={rowHeaderColumn}
      />

      {/* Pagination (desktop) */}
      {showPagination && (
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          limit={limit}
          onLimitChange={onLimitChange}
          limitOptions={limitOptions}
          isCompact={false}
        />
      )}
    </div>
  )
}

ResponsiveTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.node,
    align: PropTypes.oneOf(['left', 'center', 'right']),
    sortable: PropTypes.bool,
    renderCell: PropTypes.func,
    renderSortIcon: PropTypes.func,
    cellClassName: PropTypes.string,
    style: PropTypes.object,
    cellStyle: PropTypes.object,
    headerCellProps: PropTypes.object,
    cellProps: PropTypes.object
  })),
  data: PropTypes.array,
  loading: PropTypes.bool,
  searched: PropTypes.bool,
  sortField: PropTypes.string,
  sortDirection: PropTypes.string,
  onSort: PropTypes.func,
  onRowClick: PropTypes.func,
  variant: PropTypes.string,
  emptyState: PropTypes.node,
  loadingState: PropTypes.node,
  colSpan: PropTypes.number,
  containerProps: PropTypes.object,
  tableProps: PropTypes.object,

  headerTitle: PropTypes.string,
  headerIcon: PropTypes.node,
  headerResultCount: PropTypes.number,
  headerResultLabel: PropTypes.string,
  headerActions: PropTypes.node,
  renderHeaderContent: PropTypes.func,

  page: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  limit: PropTypes.number,
  onLimitChange: PropTypes.func,
  limitOptions: PropTypes.arrayOf(PropTypes.number),

  rowHeaderColumn: PropTypes.number
}

export default ResponsiveTable
